import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import cx from "classnames";

import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ViewCourseContent } from "./content";

import { AssessmentView } from "./content/assessment/view";
import { completeCourse, invalidateCompleteCourse, updateCourseSession } from "src/redux/actions/coaching/learners";
import { LearningRecordCourse } from "src/types/coaching/learners";
import Button from "src/components/button";
import { CourseContext } from "../context";
import { ElementTimerComponent } from "src/components/elementTimer";
import { AssessmentRow, LessonRow } from "../components/cards";

import styles from "./structure.module.scss";
import LinesEllipsis from "react-lines-ellipsis";
import Tooltip from "src/components/tooltip";
import Text from "src/components/text";

interface CourseStructureComponentProps {
  course: LearningRecordCourse;
}

export const CourseStructureComponent: React.FC<CourseStructureComponentProps> = ({ course }) => {
  const dispatch = useDispatch();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const courseContext = React.useContext(CourseContext);

  const durationRef = React.useRef<any>();

  const contents = useMemo(() => course?.course?.contents || [], [course]);

  const getIndexForLesson = (lesson) => {
    const lessons = contents?.filter((content) => content.entityType === 'lesson');
    return _.findIndex(lessons, (content) => content.uuid === lesson);
  }

  const handleBackClicked = () => {
    if (courseContext.onBack) {
      courseContext.onBack();
    }
  }

  const handleContentClicked = (index) => {
    if (courseContext.onContentClicked) {
      courseContext.onContentClicked(index);
    }
  }

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleEndCourse = () => {
    const { change } = durationRef.current.duration();

    dispatch(completeCourse({
      id: course.id!,
      duration: change,
    }));
  }

  const handleDurationChange = (time: number, change: number) => {
    console.log(`Duration changed to ${time} seconds. Change: ${change}`);
    dispatch(updateCourseSession({
      id: course.id,
      duration: time,
    }));
  }

  if (courseContext.completedCourse) {
    return (
      <CourseCompletionDialog course={course} />
    )
  }

  if (courseContext.activeAssessment) {
    return (
      <AssessmentView
        course={course}
        assessment={courseContext.activeAssessment}
      />
    )
  }

  return (
    <ElementTimerComponent id={course.id!} ref={durationRef} onDurationChange={handleDurationChange}>
      <div className={cx(styles.editContainer, { [styles.sidebarCollapsed]: sidebarCollapsed })}>        
        <Tooltip content="Toggle sidebar" placement="right">
          <button className={styles.toggleButton} onClick={toggleSidebar}>
            <i className={`paperflite-side-panel`}></i>
          </button>
        </Tooltip>

        <div className={styles.left}>
          <div className={styles.content}>
            <div className={styles.header}>
              <button
                className={styles.backButton}
                onClick={handleBackClicked}
              >
                <i className={"paperflite-left-arrow"}></i>
                <Text className={styles.courseName} text={course?.course?.name || ""} lines={1} tooltip={{ placement: 'bottom' }} />
              </button>

              <div className={styles.titleBar}>
                <h2 className={styles.title}>Course Structure</h2>
              </div>
            </div>
            <div className={styles.contentsList}>
              {contents?.map((content, index) => {
                const type = _.get(content, "entityType");
                if (type === 'lesson') {
                  const lessonIndex = getIndexForLesson(content.uuid);
                  return <LessonRow key={index}
                    className={styles.contentCard}
                    lessonIndex={lessonIndex}
                    course={course}
                    lesson={content}
                    index={index}
                    active={false}
                    onClick={handleContentClicked}
                  />
                } else if (type === 'assessment') {
                  return <AssessmentRow key={index}
                    className={styles.contentCard}
                    course={course}
                    assessment={content}
                    index={index}
                    active={false}
                    onClick={handleContentClicked}
                  />
                }
              })}

              {course.status !== 'COMPLETED' && (
                <div className={styles.footer}>
                  <Button variant="gradient" className={styles.completeButton}
                    icon="paperflite-tick"
                    text={courseContext.completingCourse ? 'Ending...' : 'End course'}
                    disabled={courseContext.completingCourse || !courseContext.canCompleteCourse}
                    onClick={() => handleEndCourse()}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.right}>
          <ViewCourseContent
            course={course}
            collapsed={sidebarCollapsed}
          />
        </div>
      </div>
    </ElementTimerComponent>
  );
};


interface CourseCompletionDialogProps {
  course: LearningRecordCourse;
}

const CourseCompletionDialog: React.FC<CourseCompletionDialogProps> = ({ course }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleExit = () => {
    navigate(`/coaching/courses`);
  }

  useEffect(() => {
    return () => {
      dispatch(invalidateCompleteCourse(course.id));
    }
  }, []);

  return (
    <div className={cx(styles.completeDialog)}>
      <div className={styles.body}>
        <img src="/images/coaching/complete_course.svg" alt="Course completed" />
        <h3>Congratulations! <br></br>you’ve successfully completed the course</h3>
        <div className={styles.actions}>
          <Button variant="gradient" icon="paperflite-tick" text="View Report" onClick={() => { }} />
          <Button variant="secondary" icon="paperflite-close" text="Exit" onClick={handleExit} />
        </div>
      </div>
    </div>
  );
}